import { Buffer } from "buffer";
import { Transaction, TokenFeeScheduleUpdateTransaction } from "@hashgraph/sdk";
import { getNetwork } from "./wallets/walletconnect/walletConnectClient";
import { networkConfig } from "../config/networks";
import { proto } from "@hashgraph/proto";

type MirrorTokenDetails = {
  fee_schedule_key?: { _type?: string; key?: string } | null;
};

function assertOkResponse(res: Response, bodyText?: string): void {
  if (res.ok) return;
  throw new Error(
    `Mirror node HTTP ${res.status} ${res.statusText}${
      bodyText ? `: ${bodyText.slice(0, 300)}` : ""
    }`
  );
}

function normalizeHex(hexRaw: unknown): string {
  if (typeof hexRaw !== "string") {
    throw new Error(
      `fee_schedule_key.key is not a string (got ${typeof hexRaw})`
    );
  }
  const hex = hexRaw.replace(/^0x/i, "").trim();
  if (hex.length === 0) throw new Error("fee_schedule_key.key is empty");
  if (hex.length % 2 !== 0)
    throw new Error("fee_schedule_key.key has odd length");
  if (/[^0-9a-f]/i.test(hex))
    throw new Error("fee_schedule_key.key contains non-hex characters");
  return hex;
}

function thresholdFromProtobufKeyHex(hex: string): number {
  const bytes = Uint8Array.from(Buffer.from(normalizeHex(hex), "hex"));
  const pbKey = proto.Key.decode(bytes);
  const t = pbKey.thresholdKey?.threshold ?? 0;
  return Number(t) || 0;
}

function requiredFromFeeScheduleKey(
  feeKey: { _type?: string; key?: string } | null | undefined
): number {
  if (!feeKey || !feeKey._type) return 0;

  // single key
  if (feeKey._type === "ED25519" || feeKey._type === "ECDSA_SECP256K1") {
    return 1;
  }

  // threshold key
  if (feeKey._type === "ProtobufEncoded") {
    if (!feeKey.key) return 0;
    return thresholdFromProtobufKeyHex(feeKey.key);
  }

  return 0;
}

async function fetchJsonWithTimeout(url: string, timeoutMs: number) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      assertOkResponse(res, text);
    }
    return (await res.json()) as unknown;
  } catch (e: any) {
    if (e?.name === "AbortError")
      throw new Error("Mirror node request timed out");
    throw e;
  } finally {
    clearTimeout(timeout);
  }
}

export async function computeRequiredSignatures(
  tx: Transaction
): Promise<number> {
  if (!(tx instanceof TokenFeeScheduleUpdateTransaction)) {
    throw new Error(
      "Unsupported transaction type for required signature calculation"
    );
  }

  const tokenId = tx.tokenId?.toString();
  if (!tokenId) {
    throw new Error("TokenFeeScheduleUpdateTransaction.tokenId is not set");
  }

  const mirrorUrl = `${
    networkConfig[getNetwork()].mirrorNodeUrl
  }/api/v1/tokens/${tokenId}`;

  const tokenDetails = (await fetchJsonWithTimeout(
    mirrorUrl,
    12_000
  )) as MirrorTokenDetails;
  const required = requiredFromFeeScheduleKey(tokenDetails?.fee_schedule_key);

  if (required <= 0) {
    throw new Error(
      "Token fee_schedule_key is missing/unsupported or threshold is 0"
    );
  }

  return required;
}
